import Vue from 'vue'
import projectApi from '@/api/project.api'

const state = {
  currentId: null,
  shortProjects: {},
  projects: {},
  projectsTotalCount: 0
}


const getters = {
  projectById: (state) => (id) => {
    return state.projects[id]
  },

  currentProjectId: (state) => {
    return state.currentId
  },

  currentProject: (state) => {
    return state.shortProjects[state.currentId] || {}
  },

  projects: (state) => {
    return Object.values(state.projects) || []
  },

  allProjects: (state) => {
    return Object.values(state.shortProjects) || []
  },

  projectsBySeason: (state, getters, rootState) => {
    return getters.allProjects.filter(x => x.seasonId === rootState.season.currentId)
  },

  searchSuggestions: (state, getters, rootState, rootGetters) => {
    const items = []

    getters.allProjects.forEach(project => {
      items.push(project.title)
    })

    getters.allProjects.forEach(project => {
      if (project.projectNumber) {
        items.push(project.projectNumber)
      }
    })

    for (const label of rootGetters['projectLabel/projectLabels']) {
      items.push(label.name)
    }

    return items
  },

  searchSuggestionsOnlyThisManager: (state, getters, rootState, rootGetters) => {
    const labelsOnlyThisManager = []
    const items = getters.allProjects.filter(project => project.isManager).map(project => {
      if (project.labels.length) {
        project.labels.forEach(label => {
          if (!labelsOnlyThisManager.includes(label.name)) {
            labelsOnlyThisManager.push(label.name)
          }
        })
      }
      return project.title
    })
    labelsOnlyThisManager.forEach(labelName => {
      items.push(labelName)
    })
    return items
  },

  projectTitleById: (state) => (id) => {
    return state.shortProjects[id].title
  },

  shortProjectById: (state) => (id) => {
    return state.shortProjects[id] || {}
  }
}


const actions = {
  async loadProjects ({ commit, rootState }, { skip, take, search, sortBy, sortDesc, onlyThisManager }) {
    const response = await projectApi.getProjects(rootState.tenant.current, rootState.season.currentId, skip, take, search, sortBy, sortDesc, onlyThisManager)
    commit('SET_PROJECTS', { projects: response.data, count: parseInt(response.headers['x-total-count']) })
  },

  async loadAllProjects ({ commit, rootState }) {
    const response = await projectApi.getShortProjects(rootState.tenant.current)
    commit('SET_SHORT_PROJECTS', response.data)
  },

  setCurrentProject ({ commit, getters }, id) {
    if ((!id && getters.projectsBySeason.length > 0) || !getters.projectsBySeason.filter(project => project.id === id).length) {
      id = getters.projectsBySeason[0].id
    }
    commit('SET_CURRENT_PROJECT', id)
  },

  async loadProjectById ({ rootState }, id) {
    const response = await projectApi.getProject(rootState.tenant.current, id)
    return response.data
  },

  async loadAndUpdateProject ({ commit, rootState }, entityData) {
    const response = await projectApi.getProject(rootState.tenant.current, entityData.entityId)
    commit('UPDATE_PROJECT', response.data)
    return response.data
  },

  async loadProjectByTenantById ({ rootState }, { tenant, id }) {
    const response = await projectApi.getProject(tenant, id)
    return response.data
  },

  async createProject ({ commit, dispatch, rootState }, project) {
    const response = await projectApi.createProject(rootState.tenant.current, project)
    commit('CREATE_PROJECT', response.data)
    dispatch('projectLabel/loadProjectLabels', null, { root: true })
  },

  async setProjectIsPublic ({ commit, rootState }, { projectId, isPublic }) {
    const response = await projectApi.setProjectIsPublic(rootState.tenant.current, projectId, isPublic)
    commit('UPDATE_PROJECT', response.data)
  },

  async setProjectStatusId ({ commit, rootState }, { projectId, statusId }) {
    const response = await projectApi.setProjectStatusId(rootState.tenant.current, projectId, statusId)
    commit('UPDATE_PROJECT', response.data)
  },

  async setProjectBookingStatusId ({ commit, rootState }, { projectId, bookingStatusId }) {
    const response = await projectApi.setProjectBookingStatusId(rootState.tenant.current, projectId, bookingStatusId)
    commit('UPDATE_PROJECT', response.data)
  },

  async updateProject ({ commit, dispatch, rootState }, project) {
    const response = await projectApi.updateProject(rootState.tenant.current, project.id, project)
    commit('UPDATE_PROJECT', response.data)
    dispatch('projectLabel/loadProjectLabels', null, { root: true })
  },

  // async updateProjectDataById ({ commit, dispatch, rootState }, projectData) {
  //   commit('UPDATE_PROJECT', projectData)
  //   // dispatch('projectLabel/loadProjectLabels', null, { root: true })
  // },

  async setProjectScheme ({ commit, rootState }, { projectId, schemeId }) {
    const response = await projectApi.setProjectScheme(rootState.tenant.current, projectId, schemeId)
    commit('UPDATE_PROJECT', response.data)
  },

  async deleteProject ({ commit, rootState }, { id, confirmTitle }) {
    await projectApi.deleteProject(rootState.tenant.current, id, confirmTitle)
    commit('DELETE_PROJECT', id)
  },

  removeLabelFromAllProjects ({ commit }, labelId) {
    const projects = Object.values(state.projects)
    for (const project of projects) {
      commit('REMOVE_PROJECT_LABEL', { projectId: project.id, labelId })
    }
  }
}


const mutations = {
  SET_PROJECTS (state, { projects, count }) {
    state.projects = {}
    for (const project of projects) {
      Vue.set(state.projects, project.id, project)
    }
    state.projectsTotalCount = count
  },

  SET_SHORT_PROJECTS (state, projects) {
    state.shortProjects = {}
    for (const project of projects) {
      Vue.set(state.shortProjects, project.id, project)
    }
  },

  SET_CURRENT_PROJECT (state, id) {
    state.currentId = id
  },

  CREATE_PROJECT (state, project) {
    state.projectsTotalCount = state.projectsTotalCount + 1
    Vue.set(state.projects, project.id, project)

    Vue.set(state.shortProjects, project.id, {
      id: project.id,
      title: project.title,
      seasonId: project.seasonId,
      schemeId: project.schemeId,
      projectNumber: project.projectNumber
    })
  },

  UPDATE_PROJECT (state, project) {
    Vue.set(state.projects, project.id, project)

    Vue.set(state.shortProjects, project.id, {
      id: project.id,
      title: project.title,
      seasonId: project.seasonId,
      schemeId: project.schemeId,
      projectNumber: project.projectNumber
    })
  },

  DELETE_PROJECT (state, id) {
    state.projectsTotalCount = state.projectsTotalCount - 1
    Vue.delete(state.projects, id)
    Vue.delete(state.shortProjects, id)
  },

  REMOVE_PROJECT_LABEL (state, { projectId, labelId }) {
    const labelIndex = state.projects[projectId].labels.findIndex(x => x === labelId)
    if (labelIndex !== -1) {
      state.projects[projectId].labels.splice(labelIndex, 1)
    }
  }
}


export default {
  state,
  getters,
  actions,
  mutations
}
